import React, { useState, useEffect, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import api, { isLocalhostAccess } from './api/api';
import { AppShell } from './components/layout';

// 页面组件
import DashboardPage from './pages/DashboardPage';
import LogsPage from './pages/LogsPage';
import PageVisitTracker from './components/PageVisitTracker';
import AuditLogsPage from './pages/AuditLogsPage';
import RulesPage from './pages/RulesPage';
import NewRulePage from './pages/NewRulePage';
import FieldsPage from './pages/FieldsPage';
import PersonsPage from './pages/PersonsPage';
import AISettingsPage from './pages/AISettingsPage';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import WecomPage from './pages/WecomPage';
import WecomChatLogsPage from './pages/WecomChatLogsPage';
import CalendarsPage from './pages/CalendarsPage';
import UsersPage from './pages/UsersPage';
import QueryTriggerTasksPage from './pages/QueryTriggerTasksPage';
import JiraSettingsPage from './pages/JiraSettingsPage';
import KADashboardPage from './pages/KADashboardPage';
import KASettingsPage from './pages/KASettingsPage';
import KAProductDomainsPage from './pages/KAProductDomainsPage';
import KAPendingPage from './pages/KAPendingPage';
import { hasPermission } from './utils/permissions';

// 创建 Auth Context
export const AuthContext = React.createContext(null);

const createLocalAdminUser = () => ({
  id: 0,
  email: 'local-admin@localhost',
  username: 'Local Admin',
  is_admin: true,
  is_active: true,
  group_id: null,
  group_name: null,
  permissions: []
});

function PermissionRoute({ permission, children }) {
  const auth = useContext(AuthContext);
  const user = auth?.user;

  if (!hasPermission(user, permission)) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <h2 style={{ margin: '0 0 12px' }}>无权访问</h2>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>
            当前账号没有访问该页面的权限，请联系管理员开通。
          </p>
        </div>
      </div>
    );
  }

  return children;
}

// 根地址也是登录和品牌按钮的统一入口，等待认证完成后按权限选择页面。
export function HomeRoute() {
  const { user } = useContext(AuthContext);
  if (!hasPermission(user, 'rules:read') && hasPermission(user, 'ka_dashboard:read')) {
    return <Navigate to="/ka-dashboard" replace />;
  }
  return (
    <PermissionRoute permission="rules:read">
      <DashboardPage canReadJiraSettings={hasPermission(user, 'jira_settings:read')} />
    </PermissionRoute>
  );
}

// 受保护的路由
function ProtectedRoute({ children }) {
  const localAccess = isLocalhostAccess();
  const token = localStorage.getItem('access_token');
  const [isAuthenticated, setIsAuthenticated] = useState(localAccess || !!token);
  const [isLoading, setIsLoading] = useState(!localAccess);
  const [user, setUser] = useState(localAccess ? createLocalAdminUser() : null);

  useEffect(() => {
    const checkAuth = async () => {
      if (isLocalhostAccess()) {
        setUser(createLocalAdminUser());
        setIsAuthenticated(true);
        setIsLoading(false);
        return;
      }

      const token = localStorage.getItem('access_token');
      if (!token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        const response = await api.getCurrentUser();
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (err) {
        console.error('认证检查失败:', err);
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (isLoading) {
    return (
      <div className="app-container">
        <div className="loading">
          <div className="loading-spinner" />
          <span>加载中...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <PageVisitTracker userId={user?.id} />
      {children}
    </AuthContext.Provider>
  );
}

// 主布局
function MainLayout() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      if (isLocalhostAccess()) {
        setUser(createLocalAdminUser());
        return;
      }

      try {
        const response = await api.getCurrentUser();
        setUser(response.data);
      } catch (err) {
        console.error('获取用户信息失败:', err);
      }
    };
    fetchUser();
  }, []);

  return (
    <AppShell user={user} onLogout={() => setUser(null)}>
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/audit-logs" element={<PermissionRoute permission="audit:read"><AuditLogsPage /></PermissionRoute>} />
          <Route path="/logs" element={<LogsPage canCancelQueryTriggers={hasPermission(user, 'query_triggers:cancel')} />} />
          <Route path="/query-trigger-tasks" element={<QueryTriggerTasksPage canCancelQueryTriggers={hasPermission(user, 'query_triggers:cancel')} />} />
          <Route path="/rules" element={<RulesPage canWriteGroups={hasPermission(user, 'groups:write')} />} />
          <Route path="/rules/new" element={<NewRulePage />} />
          <Route path="/rules/edit/:id" element={<NewRulePage />} />
          <Route path="/fields" element={<FieldsPage />} />
          <Route path="/persons" element={<PersonsPage />} />
          <Route path="/calendars" element={<CalendarsPage />} />
          <Route path="/users" element={<PermissionRoute permission="users:read"><UsersPage isAdmin={Boolean(user?.is_admin)} canWriteUsers={hasPermission(user, 'users:write')} /></PermissionRoute>} />
          <Route path="/ai-settings" element={<PermissionRoute permission="ai_settings:read"><AISettingsPage canWrite={hasPermission(user, 'ai_settings:write')} /></PermissionRoute>} />
          <Route path="/wecom" element={<PermissionRoute permission="wecom:read"><WecomPage canWrite={hasPermission(user, 'wecom:write')} /></PermissionRoute>} />
          <Route path="/wecom/chat-logs" element={<PermissionRoute permission="wecom:read"><WecomChatLogsPage /></PermissionRoute>} />
          <Route path="/jira-settings" element={<PermissionRoute permission="jira_settings:read"><JiraSettingsPage canWrite={hasPermission(user, 'jira_settings:write')} /></PermissionRoute>} />
          <Route path="/ka-dashboard" element={<PermissionRoute permission="ka_dashboard:read"><KADashboardPage /></PermissionRoute>} />
          <Route path="/ka-pending" element={<PermissionRoute permission="ka_settings:read"><KAPendingPage canWrite={hasPermission(user, 'ka_settings:write')} /></PermissionRoute>} />
          <Route path="/ka-domains" element={<PermissionRoute permission="ka_settings:read"><KAProductDomainsPage canWrite={hasPermission(user, 'ka_settings:write')} /></PermissionRoute>} />
          <Route path="/ka-settings" element={<PermissionRoute permission="ka_settings:read"><KASettingsPage canWrite={hasPermission(user, 'ka_settings:write')} /></PermissionRoute>} />
          <Route path="/profile" element={<ProfilePage />} />
        </Routes>
    </AppShell>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;
